import { createTextWidthMeasurer } from "./createTextMeasurer.ts";

/**
 * Character-based middle truncation
 */
export function truncateMiddle(text: string, maxLength: number, ellipsis = "…"): string {
  if (text.length <= maxLength) return text;

  const ellipsisLength = ellipsis.length;
  const availableLength = maxLength - ellipsisLength;

  if (availableLength <= 0) return ellipsis.slice(0, maxLength);

  const leftLength = Math.ceil(availableLength / 2);
  const rightLength = availableLength - leftLength;
  const rightPart = rightLength > 0 ? text.slice(-rightLength) : "";

  return text.slice(0, leftLength) + ellipsis + rightPart;
}

/**
 * Fast overflow detection - only checks once on mount
 */
export function truncateByOverflow(
  element: HTMLElement,
  text: string,
  ellipsis = "…",
  shouldCancel?: () => boolean,
): string {
  const buffer = 0.5;
  const containerWidth = element.clientWidth;
  if (containerWidth === 0) {
    return text;
  }

  const measure = createTextWidthMeasurer(element);
  const fits = (candidate = text) => {
    return measure(candidate) - containerWidth <= buffer;
  };

  if (fits()) {
    return text;
  }

  let maxLength = text.length;
  let minLength = 0;

  while (minLength < maxLength) {
    if (shouldCancel?.()) {
      return text;
    }

    const midLength = Math.ceil((minLength + maxLength) / 2);
    const truncated = truncateMiddle(text, midLength, ellipsis);

    if (fits(truncated)) {
      minLength = midLength;
    } else {
      maxLength = midLength - 1;
    }
  }

  let finalText = truncateMiddle(text, minLength, ellipsis);

  if (!finalText) {
    const minimal = truncateMiddle(text, 1, ellipsis) || ellipsis || text;
    return minimal;
  }

  // If rounding still clips, drop one more character.
  if (!fits(finalText) && minLength > 0 && !shouldCancel?.()) {
    finalText = truncateMiddle(text, minLength - 1, ellipsis);
  }

  return finalText;
}
